import { FC, HTMLProps } from 'react';
import { RelativeTime } from 'ui/utility/relativeTime';
import { MessageMedium } from 'modules/domains/campaign/types';
import { MESSAGE_MEDIUM_CONTENT_MAPPER } from 'modules/campaigns/lib/utils';
import {
  Item,
  Metadata,
  RelativeTimeData,
  Title,
  StyledNeutralBadge,
  StyledLink,
  StyledResourcePreview,
} from './ActivityItem.styled';
import { ActivityItem as ActivityItemType, ResourceData } from '../Launchpad.types';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';
import { ActivityItemLoading } from './ActivityItemLoading';
import {
  ACTION_TYPE_TO_DISPLAY_TEXT_MAP,
  ACTIVITY_ITEM_TEST_IDS,
  CAMPAIGN_METADATA_ICON_SIZE,
  MAX_ITEM_TITLE_WIDTH,
} from './ActivityItem.constants';

type ActivityItemProps = {
  hasError?: boolean;
  isHidden?: boolean;
  isLoading?: boolean;
  isThumbnailLoading?: boolean;
  item: ActivityItemType;
  resource: ResourceData;
  thumbnail?: string;
  url?: string;
  titleWidth?: number;
};

export const ActivityItem = ({
  isHidden = false,
  hasError = false,
  isLoading = false,
  isThumbnailLoading = false,
  item,
  resource,
  thumbnail,
  url = '/',
  titleWidth = MAX_ITEM_TITLE_WIDTH,
}: ActivityItemProps) => {
  const { resourceType } = item;
  const { name } = resource || {};
  const messageMedium = 'messageMedium' in resource ? resource.messageMedium : undefined;
  const templateId = 'templateId' in resource ? resource.templateId : undefined;
  const { Icon: MessageIcon } = MESSAGE_MEDIUM_CONTENT_MAPPER[messageMedium as MessageMedium] || {};

  if (isLoading) return <ActivityItemLoading />;
  if (hasError || isHidden) return <></>;

  return (
    <Item data-test={ACTIVITY_ITEM_TEST_IDS.ITEM}>
      <StyledResourcePreview
        resourceType={resourceType}
        thumbnailUrl={thumbnail}
        isThumbnailLoading={isThumbnailLoading}
        messageMedium={messageMedium}
        templateId={templateId}
        resourceUrl={url}
        data-test={ACTIVITY_ITEM_TEST_IDS.THUMBNAIL}
      />

      <Title data-test={ACTIVITY_ITEM_TEST_IDS.ITEM_NAME}>
        <TruncateTooltip
          link={url}
          shouldLinkOpenInSameWindow
          LinkComponent={StyledLink as FC<React.PropsWithChildren<HTMLProps<HTMLAnchorElement>>>}
          name={name}
          maxWidth={titleWidth}
        />
      </Title>
      <Metadata>
        <StyledNeutralBadge
          data-test={ACTIVITY_ITEM_TEST_IDS.ITEM_TYPE}
          icon={
            messageMedium ? (
              <MessageIcon
                data-test={`${ACTIVITY_ITEM_TEST_IDS.MEDIUM_ICON}-${messageMedium}`}
                {...CAMPAIGN_METADATA_ICON_SIZE}
              />
            ) : (
              <></>
            )
          }
        >
          {resourceType}
        </StyledNeutralBadge>
        <RelativeTimeData data-test={ACTIVITY_ITEM_TEST_IDS.ACTION_TYPE}>
          {ACTION_TYPE_TO_DISPLAY_TEXT_MAP[item.actionType]}{' '}
          <RelativeTime timestamp={item.trackedAt} />
        </RelativeTimeData>
      </Metadata>
    </Item>
  );
};
